// ScadenzarioView.jsx — amm-v3 (A2, ondata 29): la vista "Regole degli
// obblighi" (pagina 'scadenzario' — il CATALOGO delle regole, spike o27 §1.2)
// in React, flag-gated (shell_amm_v3_scadenzario, default OFF).
//
// Replica 1:1 renderScadenzario di admin-module.js r.1635-1704: stesso titolo
// (col tooltip "Tecnicamente 'Catalogo obblighi'"), stesso sottotitolo, badge
// "N regole attive" (conta le EFFETTIVE sul catalogo intero), banner proposte
// in attesa (click → coda v1), stessa barra filtri combinata A5-032
// (testo+gruppo+solo effettive, delegata ad AMM.regoleFiltrate), stesse righe
// (titolo, codice·riferimento, periodicità+badge trigger+⚠ da confermare,
// ricorrenza, 'non applicabile', Registra evento, ✎ modifica, toggle attiva),
// stesso footer sulle regole grigie.
//
// La lista è il DataTable condiviso (mandato A2); i MODALI sono DELEGATI al
// v1: editRegola (editor + anteprima obbligatoria), apriModaleRegolaAI,
// apriCodaProposte. I loro refresh post-save (renderScadenzario(mountEl),
// BYPASS di renderPage) sono reindirizzati dall'interceptor del motore
// (install.js) su renderPage ⇒ questa vista ricarica i dati.
//
// QUIRK v1 REPLICATI (parità bug-compatibile, pinnati nei test):
//  - i filtri si AZZERANO a ogni redraw della pagina (nel v1 `filtro` è una
//    variabile locale di renderScadenzario: ogni re-render riparte pulito);
//  - una regola con gruppo sconosciuto NON compare (regoleOrdinate);
//  - 'Registra evento' NON ricarica la lista (il v1 non lo fa, r.1684-1686).
//
// DEVIAZIONI DICHIARATE (v3 non bug-compatibile qui):
//  D-A2.1 doppio click su toggle/Registra evento = 1 richiesta (guardia ref
//         per riga; il v1 non guarda nessuno dei due).
//  D-A2.2 durante il refresh le righe presenti restano visibili + banner
//         'Dati non aggiornati' con Riprova a ricarica fallita (precedenti
//         D-A1.2/D-A1.5).
//  D-A2.3 la lista è una TABELLA con intestazioni (DataTable, mandato):
//         il raggruppamento visivo per gruppo del v1 diventa la colonna
//         'Gruppo' (ordine righe IDENTICO: gruppi del catalogo, poi ordine
//         lista); intestazioni = testi NUOVI dichiarati (AS_NEW_TEXTS).
//  D-A2.4 la colonna Ricorrenza resta visibile anche <768px (il v1 la
//         nasconde con hidden md:block; il DataTable scorre nel wrapper).

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import { AM_NEW_TEXTS } from './api.js';
import {
  AS_TEXTS, AS_NEW_TEXTS, AS_COLORS,
  contaAttive, regoleOrdinate, isDaConfermare, subRegola, confermaEventoTesto,
  readScadenzario, toggleRegola, generaScadenzaRegola,
  apriEditorRegola, apriNuovaRegolaAI, apriCodaProposteV1, refreshPagina,
} from './scadenzario-api.js';

const STYLE_ID = 'amm-v3-scadenzario-style';
function injectStyle(doc) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  // parità visiva r.1619: la riga NON effettiva è a mezza opacità (regola grigia)
  st.textContent = '.amm-v3-reg-off .dt-td{opacity:.5}';
  doc.head.appendChild(st);
}

// HTML prodotto dalle primitive v1 (già escapato da AMM.esc): iniettato verbatim
function V1Html({ html }) {
  if (!html) return null;
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export function ScadenzarioView({ win, tick }) {
  const A = win.AMM;
  const [dati, setDati] = React.useState({ loading: true, error: null, regole: [], proposte: [] });
  const [reloadN, setReloadN] = React.useState(0);
  // QUIRK v1: filtro locale alla vista, azzerato a ogni redraw (vedi header)
  const [filtro, setFiltro] = React.useState({ testo: '', gruppo: '', soloEffettive: false });
  const busyRows = React.useRef(new Set()); // guardia SINCRONA anti doppio-click per riga (D-A2.1)

  React.useEffect(() => {
    injectStyle(win.document);
  }, [win]);

  React.useEffect(() => {
    let vivo = true;
    setDati((d) => ({ ...d, loading: true, error: null }));
    readScadenzario(win).then(
      (r) => {
        if (vivo) {
          setDati({ loading: false, error: null, ...r });
          setFiltro({ testo: '', gruppo: '', soloEffettive: false }); // parità: il v1 riparte pulito a ogni render
        }
      },
      (e) => { if (vivo) setDati((d) => ({ ...d, loading: false, error: e })); }
    );
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win, tick, reloadN]);

  const GRP = A.ammGruppiCatalogo();
  const attive = contaAttive(dati.regole);
  const filtrate = A.regoleFiltrate(dati.regole, filtro);
  const rows = regoleOrdinate(filtrate, Object.keys(GRP));

  const conGuardia = (chiave, fn) => async () => {
    if (busyRows.current.has(chiave)) return;
    busyRows.current.add(chiave);
    try { await fn(); } finally { busyRows.current.delete(chiave); }
  };

  const onToggle = (r) => conGuardia('tog:' + r.codice, async () => {
    try {
      await toggleRegola(win, r.codice, !r.attiva); // parità r.1670: {attiva: !nowOn}
      A.toast(AS_TEXTS.toastRegolaAggiornata);
      await refreshPagina(win); // stessa via del v1 (redraw della pagina)
    } catch (e) {
      win.alert(AS_TEXTS.errorePrefix + (e && e.message ? e.message : e)); // verbatim r.1671
    }
  })();

  const onEvento = (r) => {
    // conferma PRIMA della guardia (come il v1: il confirm sincrono è il primo "attendere")
    if (!win.confirm(confermaEventoTesto(r.titolo))) return;
    return conGuardia('ev:' + r.codice, async () => {
      try {
        await generaScadenzaRegola(win, r.codice);
        A.toast(AS_TEXTS.toastScadenzaCreata);
        // parità r.1684-1686: il v1 NON ricarica la lista qui
      } catch (e) {
        win.alert(AS_TEXTS.errorePrefix + (e && e.message ? e.message : e));
      }
    })();
  };

  const columns = [
    {
      key: '_gruppo',
      label: AS_NEW_TEXTS.colGruppo,
      render: (r) => (
        <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: AS_COLORS.attiveFg }}>{GRP[r.gruppo]}</span>
      ),
    },
    {
      key: '_regola',
      label: AS_NEW_TEXTS.colRegola,
      render: (r) => (
        <div className="min-w-0">
          <div className="text-sm font-medium">{r.titolo}</div>
          <div className="text-[11px] text-slate-400 font-mono">{subRegola(r)}</div>
        </div>
      ),
    },
    {
      key: '_periodicita',
      label: AS_NEW_TEXTS.colPeriodicita,
      render: (r) => (
        <div className="text-xs text-slate-500 flex items-center gap-1">
          {r.periodicita}
          <V1Html html={A.triggerBadge(r.trigger)} />
          {isDaConfermare(r) && (
            <span className="amm-badge" style={{ background: AS_COLORS.daConfermareBg, color: AS_COLORS.daConfermareFg }}>{AS_TEXTS.daConfermare}</span>
          )}
        </div>
      ),
    },
    {
      key: '_ricorrenza',
      label: AS_NEW_TEXTS.colRicorrenza,
      render: (r) => <span className="text-[11px] text-slate-500">{A.descrizioneRegolaData(r)}</span>,
    },
    {
      key: '_applic',
      label: AS_NEW_TEXTS.colApplicabilita,
      render: (r) => (
        <span className="text-xs" style={{ color: r.effettiva ? AS_COLORS.applicOn : AS_COLORS.applicOff }}>
          {r.applicabile ? '' : AS_TEXTS.nonApplicabile}
        </span>
      ),
    },
    {
      key: '_azioni',
      label: AS_NEW_TEXTS.colAzioni,
      align: 'right',
      render: (r) => (
        <span className="whitespace-nowrap">
          {r.trigger === 'evento' && (
            <button
              type="button"
              className="amm-reg-evento text-blue-700 hover:text-blue-900 text-xs font-medium whitespace-nowrap mr-3"
              onClick={() => onEvento(r)}
            >{AS_TEXTS.registraEvento + ' →'}</button>
          )}
          <button
            type="button"
            className="amm-reg-edit text-slate-400 hover:text-cyan-700 cursor-pointer text-xs bg-transparent border-0 p-0"
            title={AS_TEXTS.editTitle}
            onClick={() => apriEditorRegola(win, r)}
          >{'✎'}</button>
        </span>
      ),
    },
    {
      key: '_attiva',
      label: AS_NEW_TEXTS.colAttiva,
      align: 'center',
      render: (r) => (
        <div
          className={'amm-tog ' + (r.attiva ? 'on' : 'off')}
          data-codice={r.codice}
          onClick={() => onToggle(r)}
        ><div className="k" /></div>
      ),
    },
  ];

  return (
    <div data-amm-v3="scadenzario">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold" title={AS_TEXTS.titleTooltip}>{AS_TEXTS.title}</h1>
          <div className="text-xs text-slate-400">{AS_TEXTS.subtitle}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="amm-badge" style={{ background: AS_COLORS.attiveBg, color: AS_COLORS.attiveFg }}>{attive + AS_TEXTS.attiveSuffix}</span>
          <button id="amm-regola-new-ai" type="button" className="bg-violet-700 hover:bg-violet-600 text-white px-3 py-1.5 rounded text-sm font-medium" onClick={() => apriNuovaRegolaAI(win)}>{AS_TEXTS.btnNuovaAi}</button>
          <button id="amm-regola-new" type="button" className="bg-cyan-700 hover:bg-cyan-600 text-white px-3 py-1.5 rounded text-sm font-medium" onClick={() => apriEditorRegola(win, {})}>{AS_TEXTS.btnNuova}</button>
        </div>
      </div>

      {dati.proposte.length > 0 && (
        <div
          id="amm-proposte-banner"
          className="cursor-pointer mb-4 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between"
          style={{ background: AS_COLORS.proposteBg, color: AS_COLORS.proposteFg, border: '1px solid ' + AS_COLORS.proposteBorder }}
          onClick={() => apriCodaProposteV1(win)}
        >
          <span>{dati.proposte.length + AS_TEXTS.proposteSuffix}</span>
          <span>{AS_TEXTS.proposteVedi + ' →'}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-2 items-center">
        <input
          id="amm-reg-filtro-testo"
          type="text"
          placeholder={AS_TEXTS.filtroTestoPlaceholder}
          className="border rounded px-3 py-1.5 text-sm w-72"
          value={filtro.testo}
          onChange={(ev) => setFiltro({ ...filtro, testo: ev.target.value })}
        />
        <select
          id="amm-reg-filtro-gruppo"
          className="border rounded px-2 py-1.5 text-sm"
          value={filtro.gruppo}
          onChange={(ev) => setFiltro({ ...filtro, gruppo: ev.target.value })}
        >
          <option value="">{AS_TEXTS.filtroTuttiGruppi}</option>
          {Object.keys(GRP).map((g) => <option key={g} value={g}>{GRP[g]}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input
            id="amm-reg-filtro-effettive"
            type="checkbox"
            className="rounded"
            checked={filtro.soloEffettive}
            onChange={(ev) => setFiltro({ ...filtro, soloEffettive: ev.target.checked })}
          />
          {' ' + AS_TEXTS.filtroSoloEffettive}
        </label>
      </div>

      {/* D-A2.2 (precedente D-A1.5): ricarica fallita con righe presenti ⇒
          l'errore si DICHIARA comunque, con Riprova accanto (mai silenzio) */}
      {dati.error && dati.regole.length > 0 && (
        <div role="alert" className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-3" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>
          <span>{AM_NEW_TEXTS.erroreRicarica + (dati.error.message || String(dati.error))}</span>
          <button type="button" className="dt-retry" style={{ marginTop: 0 }} onClick={() => setReloadN((n) => n + 1)}>{AM_NEW_TEXTS.riprova}</button>
        </div>
      )}

      <div id="amm-reg-lista">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey="codice"
          loading={dati.loading}
          error={dati.error}
          onRetry={() => setReloadN((n) => n + 1)}
          emptyMessage={AS_TEXTS.empty}
          rowClass={(r) => (r.effettiva ? '' : 'amm-v3-reg-off')}
        />
      </div>
      <p className="text-xs text-slate-400 mt-4">{AS_TEXTS.footer}</p>
    </div>
  );
}

// Factory (pattern createAmmV3Cruscotto): install.js la registra nel registry
// del wrap di AMM.renderPage. render() è idempotente sul container #amm-page;
// unmount() libera il container (il v1 resta vivo per le altre pagine).
export function createAmmV3Scadenzario(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;

  function render() {
    const container = win.document.getElementById('amm-page');
    if (!container) throw new Error('#amm-page assente');
    const manomesso = root && mountedEl === container && tick > 0
      && !container.querySelector('[data-amm-v3]');
    if (!root || mountedEl !== container || !container.isConnected || manomesso) {
      if (root) { try { root.unmount(); } catch (e) { /* root già ferito/smontato */ } }
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (nav, rientro, post-azione)
    root.render(<ScadenzarioView win={win} tick={tick} />);
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
